import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import HomeView from "../views/HomeView.vue";
import BlogsView from "@/views/Blogs/BlogsView.vue";
import BlogView from "@/views/Blog/BlogView.vue";
import { ROUTE_NAMES } from "@/constants/routeNames";
import QiMenView from "@/views/Calculators/QiMen/QiMenView.vue";
import ICzinView from "@/views/Calculators/ICzinPrediction/ICzinView.vue";
import DayRunePredictionView from "@/views/Calculators/DayRunePrediction/DayRunePredictionView.vue";
import TarotPredictionView from "@/views/Calculators/TarotPrediction/TarotPredictionView.vue";
import TarotDailyPrediction from "@/views/Calculators/TarotPrediction/DailyPrediction/TarotDailyPrediction.vue";
import TarotSituationPrediction from "@/views/Calculators/TarotPrediction/SituationPrediction/TarotSituationPrediction.vue";
import AzimuthFacadeView from "@/views/Calculators/AzimuthFacade/AzimuthFacadeView.vue";
import ActivationListView from "@/views/Calculators/Activation/ActivationListView.vue";
import ActivationFormView from "@/views/Calculators/Activation/ActivationFormView.vue";
import BaziPredictionView from "@/views/Calculators/BaZiPrediction/BaziPredictionView.vue";
import { useNavigationStore } from "@/stores/navigationStore";
import ApartmentPlanView from "@/views/Calculators/ApartmentPlan/ApartmentPlanView.vue";
import ChineseCalendarView from "@/views/Calculators/ChineseCalendar/ChineseCalendarView.vue";
import { menuItems } from "@/constants/menu";
import UserView from "@/views/Auth/UserView.vue";
import { useUserStore } from "@/stores/userStore";
import VerifyEmailView from "@/views/Auth/VerifyEmailView.vue";
import ResetPasswordView from "@/views/Auth/ResetPasswordView.vue";
import ChineseCalendarDayView from "@/views/Calculators/ChineseCalendar/ChineseCalendarDayView.vue";
import DestinyMatrixView from "@/views/Calculators/DestinyMatrix/DestinyMatrixView.vue";
import PersonalDateSelectionView from "@/views/Calculators/PersonalDates/PersonalDateSelectionView.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: HomeView,
    name: ROUTE_NAMES.HOME,
    meta: { showBgImage: false },
  },
  {
    path: "/qi-men",
    component: QiMenView,
    name: ROUTE_NAMES.QI_MEN,
    meta: { showBlogBtn: false },
  },
  {
    path: "/i-czin",
    component: ICzinView,
    name: ROUTE_NAMES.I_CZIN,
    meta: { showBlogBtn: false },
  },
  {
    path: "/day-rune-prediction",
    component: DayRunePredictionView,
    name: ROUTE_NAMES.DAY_RUNE_PREDICTION,
    meta: { showBlogBtn: false },
  },
  {
    path: "/tarot",
    meta: { showBlogBtn: false },
    children: [
      {
        path: "",
        component: TarotPredictionView,
        name: ROUTE_NAMES.TAROT,
      },
      {
        path: "daily",
        component: TarotDailyPrediction,
        name: ROUTE_NAMES.TAROT_DAILY,
      },
      {
        path: "situation-prediction",
        component: TarotSituationPrediction,
        name: ROUTE_NAMES.TAROT_SITUATION,
      },
    ],
  },
  {
    path: "/azm",
    component: AzimuthFacadeView,
    name: ROUTE_NAMES.AZM,
    meta: { showBgImage: false, showBlogBtn: false },
  },
  {
    path: "/activations",
    children: [
      {
        path: "",
        component: ActivationListView,
        name: ROUTE_NAMES.ACTIVATIONS,
      },
      {
        path: "form",
        component: ActivationFormView,
        name: ROUTE_NAMES.ACTIVATION_FORM,
        meta: { layout: "calculator" },
      },
    ],
  },
  {
    path: "/room-mapping",
    component: ApartmentPlanView,
    name: ROUTE_NAMES.ROOM_MAPPING,
    meta: { withOverflow: true, showBgImage: false, showBlogBtn: false },
  },
  {
    path: "/chinese-calendar",
    meta: { showBlogBtn: false },
    children: [
      {
        path: "",
        component: ChineseCalendarView,
        name: ROUTE_NAMES.CHINESE_CALENDAR,
      },
      {
        path: "day",
        component: ChineseCalendarDayView,
        name: ROUTE_NAMES.CHINESE_CALENDAR_DAY,
      },
    ],
  },
  {
    path: "/bazi",
    component: BaziPredictionView,
    name: ROUTE_NAMES.BAZI,
  },
  {
    path: "/blogs",
    name: ROUTE_NAMES.BLOG_LIST,
    component: BlogsView,
    meta: { layout: "empty" },
  },
  {
    path: "/blog/:id",
    name: ROUTE_NAMES.BLOG_DETAIL,
    component: BlogView,
    props: true,
    meta: { layout: "empty" },
  },
  {
    path: "/user",
    name: ROUTE_NAMES.USER,
    component: UserView,
    meta: { layout: "calculator" },
  },
  {
    path: "/verify-email",
    name: ROUTE_NAMES.VERIFY_EMAIL,
    component: VerifyEmailView,
    meta: { layout: "empty" },
  },
  {
    path: "/reset-password",
    name: ROUTE_NAMES.RESET_PASSWORD,
    component: ResetPasswordView,
    meta: { layout: "empty" },
  },
  {
    path: "/destiny-matrix",
    name: ROUTE_NAMES.DESTINY_MATRIX,
    component: DestinyMatrixView,
  },
  {
    path: "/personal-date-selection",
    name: ROUTE_NAMES.PERSONAL_DATE_SELECTION,
    component: PersonalDateSelectionView,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const navStore = useNavigationStore();
  navStore.previousRoute = from.name ? from : null;

  const userStore = useUserStore();
  const matchedItem = menuItems.find(
    (menuItem) =>
      menuItem.matchNames?.includes(to.name as never) ||
      menuItem.menuName === to.name
  );

  if (
    matchedItem !== undefined &&
    matchedItem.premiumType !== undefined &&
    !userStore.userHasSubscription(matchedItem.premiumType)
  ) {
    return next({ name: ROUTE_NAMES.HOME });
  }

  next();
});

export default router;
